class WishlistCustomer extends HTMLElement {
  constructor() {
    super();
  } 

  connectedCallback() {
    this.listId = '';
    this.enable = window.__VPV?.wishlist?.enabled || false;
    this.productId = Number(this.dataset.productId) || null;
    this.variantId = Number(this.dataset.variantId) || null;
    this.productUrl = this.dataset.productUrl;
    this.productTitle = this.dataset.productTitle;
    this.productImage = this.dataset.productImage;
    this.active = window.__VPV?.wishlist?.ids?.includes(this.productId) || false;

    this.render();
    this.updateRender();

    // Observe attribute changes so the heart stays in sync when the
    // card swaps to a sibling product via a swatch click (product id +
    // url change) or when a variant picker fires `variant:change`.
    this.attributeObserver = new MutationObserver((mutations) => {
      let changed = false;
      mutations.forEach((mutation) => {
        if (mutation.type !== 'attributes') return;
        if (mutation.attributeName === 'data-variant-id') {
          const v = Number(this.dataset.variantId) || null;
          if (v !== this.variantId) {
            this.variantId = v;
            changed = true;
          }
        } else if (mutation.attributeName === 'data-product-id') {
          const p = Number(this.dataset.productId) || null;
          if (p !== this.productId) {
            this.productId = p;
            this.active = window.__VPV?.wishlist?.ids?.includes(p) || false;
            changed = true;
          }
        } else if (mutation.attributeName === 'data-product-url') {
          this.productUrl = this.dataset.productUrl;
        } else if (mutation.attributeName === 'data-product-title') {
          this.productTitle = this.dataset.productTitle;
        } else if (mutation.attributeName === 'data-product-image') {
          this.productImage = this.dataset.productImage;
        }
      });
      if (changed) this.updateRender();
    });

    this.attributeObserver.observe(this, {
      attributes: true,
      attributeFilter: [
        'data-variant-id',
        'data-product-id',
        'data-product-url',
        'data-product-title',
        'data-product-image',
      ],
    });

    window.addEventListener('wishlist-ready', (event) => {
      this.enable = true;
      this.listId = window.__VPV?.wishlist?.listId || '';
      this.updateRender();
    });
    window.addEventListener('wishlist-update', () => {
      this.active = window.__VPV?.wishlist?.ids?.includes(this.productId) || false;
      this.updateRender();
    });

    // Listen for variant changes on product page
    this.variantChangeHandler = (event) => {
      // Only update if this wishlist element is on a product page (within .product-info)
      const isOnProductPage = this.closest('.product-info') !== null;
      if (isOnProductPage && event.detail?.variant?.id) {
        const newVariantId = Number(event.detail.variant.id);
        if (newVariantId && newVariantId !== this.variantId) {
          this.setAttribute('data-variant-id', newVariantId);
          // The MutationObserver will pick this up and update this.variantId
        }
      }
    };
    document.addEventListener('variant:change', this.variantChangeHandler);
  }

  disconnectedCallback() {
    this._wishlistControlListenerAttached = false;
    // Clean up the observer when the element is removed
    if (this.attributeObserver) {
      this.attributeObserver.disconnect();
    }
    // Clean up the variant change event listener
    if (this.variantChangeHandler) {
      document.removeEventListener('variant:change', this.variantChangeHandler);
    }
  }

  async resolveVariantId() {
    // Nosto placement payloads don't include Shopify variant IDs. When the
    // card was rendered from a Nosto rec, `data-variant-id` arrives empty
    // and Swym's `addToList` silently rejects the call because `epi` is
    // required. Fall back to the Shopify Ajax API to pull the first
    // available variant — same handle we already use for QuickAdd.
    if (this.variantId) return this.variantId;
    const handle = (this.productUrl || '').match(/\/products\/([^/?#]+)/)?.[1];
    if (!handle) return null;
    try {
      const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const json = await res.json();
      const firstAvailable = (json.variants || []).find((v) => v.available) || json.variants?.[0];
      const id = firstAvailable?.id ? Number(firstAvailable.id) : null;
      if (id) {
        this.variantId = id;
        this.setAttribute('data-variant-id', String(id));
      }
      return id;
    } catch {
      return null;
    }
  }

  async handleClick() {
    if (!window._swat) return;
    this.listId = window.__VPV?.wishlist?.listId || '';

    const variantId = await this.resolveVariantId();
    if (!variantId) {
      console.warn('[wishlist] no variant id resolved for product', this.productId);
      return;
    }

    // Swym deep URL should be absolute (`du`). Relative URLs from Nosto
    // payloads (`/products/foo`) confuse the API, so anchor to the
    // storefront origin.
    let du = this.productUrl || '';
    if (du && du.startsWith('/')) du = window.location.origin + du;

    const product = {
      epi: variantId,
      empi: this.productId,
      du,
    };

    this.active
      ? this.removeProductFromList(product)
      : this.addProductToList(product);

    this.updateRender();
    window.dispatchEvent(new CustomEvent('wishlist-update'));
  }

  generateModal(prevState) {
    console.log('🚀 -> WishlistCustomer -> generateModal -> generateModal:', prevState)
    // if (prevState) {
    //   // Modal que diga fue removido correctamente
    // } else {
    //   // Modal que diga fue agregado correctamente
    // }
    const message = 'has been added to your wishlist successfully.';


    const modalEvent = new CustomEvent('open-wishlist-modal', {
      detail: {
        title: this.productTitle,
        image: this.productImage || 'https://via.placeholder.com/150',
        message: message,
      }
    });
    window.dispatchEvent(modalEvent);
  }

  addProductToList(product) {
    window._swat.addToList(this.listId, product, this.onSuccess.bind(this, true), this.onError.bind(this));
    window.__VPV?.wishlist?.ids?.push(this.productId);
    this.generateModal(false);
  }

  removeProductFromList(product) {
    console.log('removeProductFromList', product);
    window._swat.deleteFromList(this.listId, product, this.onSuccess.bind(this, false), this.onError.bind(this));
    const index = window.__VPV?.wishlist?.ids?.indexOf(this.productId);
    if (index > -1) {
      window.__VPV.wishlist.ids.splice(index, 1);
    }
  }

  onSuccess(event, isAdded) {
    console.log('🚀 -> WishlistCustomer -> onSuccess -> Product updated:', this.productId);
    console.log(event)
    this.active = isAdded;
    this.updateRender();
  }

  onError(event) {
    console.error('🚀 -> WishlistCustomer -> onError -> Error :', this.productId);
    console.error(event);
  }

  updateRender() {
    this.active = window.__VPV?.wishlist?.ids?.includes(this.productId);
    this.classList.toggle('is-active', this.active);
    this.classList.toggle('is-enabled', this.enable);
    const btn = this.querySelector('.wishlist-customer__control');
    if (!btn) return;
    const name = WishlistCustomer.escapeForLabel(this.productTitle || 'product');
    btn.setAttribute('aria-pressed', this.active ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      this.active ? `Remove ${name} from wishlist` : `Add ${name} to wishlist`
    );
    if (this.enable) {
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('tabindex');
    } else {
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('tabindex', '-1');
    }
  }

  static escapeForLabel(text) {
    return String(text).replace(/[\u0000-\u001F"<>]/g, ' ').replace(/&/g, ' ').trim() || 'product';
  }

  render() {
    const name = WishlistCustomer.escapeForLabel(this.productTitle || 'product');
    if (!this.querySelector('.wishlist-customer__control')) {
      this.innerHTML = `
      <button type="button" class="wishlist-customer__control" aria-pressed="false" aria-label="Add ${name} to wishlist"${this.enable ? '' : ' aria-disabled="true" tabindex="-1"'}>
        <svg xmlns="http://www.w3.org/2000/svg" width="31" height="31" viewBox="0 0 31 31" fill="none" aria-hidden="true" focusable="false">
          <path d="M9.00146 5.24023C10.6719 5.23922 12.2746 5.9017 13.4565 7.08203L13.4624 7.08789L13.4692 7.09375L14.9927 8.51758L15.3335 8.83594L15.6743 8.51855L17.2085 7.08984L17.2202 7.07812C18.1022 6.20051 19.225 5.60361 20.4458 5.36328C21.6665 5.12305 22.9309 5.25077 24.0796 5.72852C25.2285 6.20638 26.2106 7.01356 26.9009 8.04883C27.5911 9.08407 27.9591 10.3007 27.9585 11.5449V11.5459C27.9603 12.3725 27.7985 13.1915 27.4819 13.9551C27.1653 14.7187 26.7001 15.4123 26.1138 15.9951L26.1099 15.999L15.645 26.6182C15.6043 26.6595 15.5559 26.6924 15.5024 26.7148C15.4489 26.7372 15.3915 26.749 15.3335 26.749C15.2755 26.749 15.218 26.7372 15.1646 26.7148C15.1111 26.6924 15.0626 26.6595 15.022 26.6182L4.55713 15.999L4.5542 15.9961L4.33838 15.7695C3.29158 14.6147 2.7075 13.109 2.70654 11.543C2.70555 9.87251 3.36793 8.26986 4.54834 7.08789C5.72886 5.90594 7.33095 5.24132 9.00146 5.24023Z" fill="currentColor" stroke="#2B2E2F"/>
        </svg>
      </button>
    `;
    }
    const control = this.querySelector('.wishlist-customer__control');
    if (control && !this._wishlistControlListenerAttached) {
      this._wishlistControlListenerAttached = true;
      control.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handleClick();
      });
    }
  }
}

customElements.define('wishlist-customer', WishlistCustomer);
